import { useCallback, useMemo } from 'react';
import { useLocalSearchParams } from 'expo-router';

import { useAppsFlyer } from '@order/integrations-appsflyer';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { getOrderPlaybackConfig } from '../../utils';

/**
 * Telemetry events for bag/checkout.
 */
export const useBagTracking = () => {
  const params = useLocalSearchParams<BagScreenParams>();
  const { track } = useTelemetry();
  const { logInitiatedCheckoutEvent } = useAppsFlyer();
  const orderPlaybackConfigFlag = useFeatureFlag('CELS-4127-order-playback-config');
  const orderPlaybackConfig = useMemo(
    () => getOrderPlaybackConfig(orderPlaybackConfigFlag),
    [orderPlaybackConfigFlag],
  );

  const trackBagView = useCallback(() => {
    track('bag.view', { entryPoint: params?.referrer });
  }, [params?.referrer, track]);

  const trackInitiatedCheckout = useCallback(
    (payload: InitiatedCheckoutPayload) => {
      logInitiatedCheckoutEvent(payload);
    },
    [logInitiatedCheckoutEvent],
  );

  const trackLocationNamePressed = useCallback(() => {
    track('bag.location-name.pressed');
  }, [track]);

  const trackWantedTimePressed = useCallback(() => {
    track('bag.wanted-time.pressed');
  }, [track]);

  const trackWantedTimeChanged = useCallback(() => {
    track('bag.wanted-time.changed');
  }, [track]);

  const trackDeliveryPreferencesPressed = useCallback(() => {
    track('bag.delivery-preferences.pressed');
  }, [track]);

  const trackDropoffLocationChanged = useCallback(() => {
    track('bag.dropoff-location.changed');
  }, [track]);

  const trackViewMenuPressed = useCallback(() => {
    track('bag.view-menu.pressed');
  }, [track]);

  const trackLineItemPressed = useCallback(() => {
    track('bag.line-item.pressed');
  }, [track]);

  const trackLineItemRemoved = useCallback(() => {
    track('bag.line-item.removed');
  }, [track]);

  const trackLineItemQuantityChanged = useCallback(() => {
    track('bag.line-item.quantity.changed');
  }, [track]);

  const trackLoyaltyInfoPressed = useCallback(() => {
    track('bag.reward-info.pressed');
  }, [track]);

  const trackLoyaltyTermsPressed = useCallback(() => {
    track('bag.reward-terms.pressed');
  }, [track]);

  const trackLoyaltySignedOutPressed = useCallback(() => {
    track('bag.loyalty-signed-out.pressed');
  }, [track]);

  const trackApplyRewardPressed = useCallback(() => {
    track('bag.apply-reward.pressed');
  }, [track]);

  const trackRemoveRewardPressed = useCallback(() => {
    track('bag.remove-reward.pressed');
  }, [track]);

  const trackPromoCodeAddPressed = useCallback(() => {
    track('bag.promo-code.add.pressed');
  }, [track]);

  const trackUtensilsUsagePressed = useCallback(() => {
    track('bag.utensils-usage.pressed');
  }, [track]);

  const trackContinuePressed = useCallback(() => {
    track('bag.continue.pressed');
  }, [track]);

  const trackContinueDeliveryMinimumBlocked = useCallback(
    (minimum: string | number, amountNeeded: string | number) => {
      track('bag.continue.delivery-minimum-blocked', { minimum, amountNeeded });
    },
    [track],
  );

  const trackChangePaymentMethodPressed = useCallback(() => {
    track('bag.change-payment-method.pressed');
  }, [track]);

  const trackCreditPressed = useCallback(() => {
    track('bag.credit.pressed');
  }, [track]);

  const trackPaymentMethodPressed = useCallback(() => {
    track('bag.payment-method.pressed');
  }, [track]);

  const trackRedeemGiftCardPressed = useCallback(() => {
    track('bag.redeem-gift-card.pressed');
  }, [track]);

  const trackRedeemGiftCardSubmitted = useCallback(() => {
    track('gift-card.redemption.submit', { source: 'bag' });
  }, [track]);

  const trackOrderPlaybackAutoSubmit = useCallback(() => {
    track('bag.order-playback.auto-submit', { orderPlaybackConfig });
  }, [orderPlaybackConfig, track]);

  const trackOrderPlaybackGoBack = useCallback(() => {
    track('bag.order-playback.go-back', { orderPlaybackConfig });
  }, [orderPlaybackConfig, track]);

  const trackUpsellExperimentStarted = useCallback(
    (placementVariation: string) => {
      track('$experiment_started', {
        'Experiment name': 'upsell_placement_v2',
        'Variant name': placementVariation,
      });
    },
    [track],
  );

  const trackUpsellModalContinuePressed = useCallback(() => {
    track('bag_upsell.modal.continue.pressed');
  }, [track]);

  const trackUpsellModalClosePressed = useCallback(() => {
    track('bag_upsell.modal.close.pressed');
  }, [track]);

  return {
    trackBagView,
    trackInitiatedCheckout,
    trackLocationNamePressed,
    trackWantedTimePressed,
    trackWantedTimeChanged,
    trackDeliveryPreferencesPressed,
    trackDropoffLocationChanged,
    trackViewMenuPressed,
    trackLineItemPressed,
    trackLineItemRemoved,
    trackLineItemQuantityChanged,
    trackLoyaltyInfoPressed,
    trackLoyaltyTermsPressed,
    trackLoyaltySignedOutPressed,
    trackApplyRewardPressed,
    trackRemoveRewardPressed,
    trackPromoCodeAddPressed,
    trackUtensilsUsagePressed,
    trackContinuePressed,
    trackContinueDeliveryMinimumBlocked,
    trackChangePaymentMethodPressed,
    trackCreditPressed,
    trackPaymentMethodPressed,
    trackRedeemGiftCardPressed,
    trackRedeemGiftCardSubmitted,
    trackOrderPlaybackAutoSubmit,
    trackOrderPlaybackGoBack,
    trackUpsellExperimentStarted,
    trackUpsellModalContinuePressed,
    trackUpsellModalClosePressed,
  };
};

// ─── Types ──────────────────────────────────────────────────────────────────

type BagScreenParams = {
  referrer?: string;
};

type InitiatedCheckoutPayload = {
  price: number;
  productIds: string;
  orderType: string;
  quantity: number;
};
